import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Form, Input, Tabs as Tab, Button, Switch } from 'antd'
import { Formik } from 'formik'
import { SearchOutlined, SaveOutlined } from '@ant-design/icons'

import Tabs from '../../Tabs'
import FormObserver from '../../../shared/FormObserver/FormObserver'

import {
  getCopyCompanies,
  resetState as resetCopyCompaniesState,
} from '../../../../state/modules/copyCompanies'
import useDispatchHttp from '../../../../hooks/dispatchHttpHandler'
import {
  LOADING_STATUS,
  DEFAULT_COPY_COMPANY_PAGE_SIZE_OPTIONS,
} from '../../../../constants'
import { primaryCopyCompanyColumns } from '../../../../data'
import Table from '../../Tables/SearchTable'

const TABS = {
  PAST: 'past',
  ALL: 'all',
}

const getRowKey = item => `${item.id}_${item.site_id || 'null'}`

const SetCopyCompanyForm = ({
  initialValues,
  onSubmit,
  entityId,
  onClose,
  isAutoDetection,
  isUserActionAllowed,
}) => {
  const dispatch = useDispatch()
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 15,
  })
  const [searchParams, setSearchParams] = useState({
    facility_profile: initialValues.id,
  })

  const [isAutoDetectionEnabled, setIsAutoDetectionEnabled] = useState(isAutoDetection)
  const [activeTab, setActiveTab] = useState(TABS.PAST)
  const dispatchHttp = useDispatchHttp()
  const copyCompaniesList = useSelector(state => state.copyCompanies.data)

  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [siteIds, setSiteIds] = useState({})

  const [initialPastKey, setInitialPastKey] = useState(null)
  const [initialPastSiteId, setInitialPastSiteId] = useState(null)

  const { count } = useSelector(state => state.copyCompanies)
  const loadingStatus = useSelector(state => state.copyCompanies.status)

  const handleSearch = useCallback(
    e => {
      const newPagination = { ...pagination, page: 1 }
      setPagination(newPagination)
      setSearchParams(state => ({ ...state, search: e.target.value }))

      dispatchHttp(
        getCopyCompanies({
          ...searchParams,
          search: e.target.value,
          ...newPagination,
        })
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatchHttp, searchParams]
  )

  const handleKeyPress = e => {
    if ((e.charCode || e.keyCode) === 13) {
      e.preventDefault()
    }
  }

  const handleChangeTab = tab => {
    const newPagination = {
      page: 1,
      pageSize: 15,
    }
    setPagination(newPagination)

    if (tab === TABS.PAST && initialPastKey) {
      setSelectedRowKeys([initialPastKey])
    } else {
      setSelectedRowKeys([])
    }

    let params
    if (tab === TABS.PAST) {
      params = {
        facility_profile: initialValues.id,
      }
    } else {
      params = {
        facility_profile: null,
      }
    }

    setSearchParams(state => ({ ...state, ...params }))
    dispatchHttp(getCopyCompanies({ ...searchParams, ...params, ...newPagination }))
    setActiveTab(tab)
  }

  useEffect(() => {
    dispatchHttp(
      getCopyCompanies({
        facility_profile: initialValues.id,
        ...pagination,
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchHttp, initialValues])

  const initValues = {
    copy_company_id: initialValues.copy_company_id,
  }
  if (copyCompaniesList) {
    copyCompaniesList.forEach(item => {
      const key = getRowKey(item)
      initValues[`site_id_${key}`] = item.site_id || null
    })
  }

  useEffect(() => {
    if (activeTab === TABS.ALL) return
    if (!copyCompaniesList) return

    const primaryCompany = copyCompaniesList.find(item => item.is_primary)

    if (primaryCompany) {
      const primaryKey = getRowKey(primaryCompany)

      if (initialPastKey === null || initialPastKey !== primaryKey) {
        setInitialPastKey(primaryKey)
        setInitialPastSiteId(primaryCompany.site_id || null)

        if (selectedRowKeys.length === 0) {
          setSelectedRowKeys([primaryKey])
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyCompaniesList, activeTab])

  useEffect(
    () => () => {
      dispatch(resetCopyCompaniesState())
    },
    [dispatch]
  )

  const formatData = useCallback(
    data =>
      data.map(item => {
        const uniqueKey = getRowKey(item)
        return {
          ...item,
          originId: item.id,
          id: uniqueKey,
          is_primary: uniqueKey === selectedRowKeys[0],
        }
      }),
    [selectedRowKeys]
  )

  const currentKey = selectedRowKeys[0]

  const isRowVisibleOnPage = copyCompaniesList
    ? copyCompaniesList.some(c => getRowKey(c) === currentKey)
    : false

  const isAutoDetectChanged = isAutoDetectionEnabled !== isAutoDetection
  const isKeyChanged = String(initialPastKey || '') !== String(currentKey || '')

  let isTextChanged = false
  if (isRowVisibleOnPage) {
    const currentInput = siteIds[`site_id_${currentKey}`]
    const currentRow = copyCompaniesList.find(c => getRowKey(c) === currentKey)
    const currentValOnPage = currentRow ? currentRow.site_id || null : null

    const valToCheck = currentInput !== undefined ? currentInput : currentValOnPage

    const clean = v => (v === null || v === undefined ? '' : String(v))
    isTextChanged = clean(valToCheck) !== clean(initialPastSiteId)
  }

  let isSaveDisabled = true

  if (activeTab === TABS.ALL) {
    isSaveDisabled = !(isRowVisibleOnPage || isAutoDetectChanged)
  } else {
    const hasChanges = isKeyChanged || isTextChanged || isAutoDetectChanged
    isSaveDisabled = !hasChanges
  }

  const primaryCopyCompanyColumnsEditable = [
    {
      title: 'Name',
      key: 'name',
    },
    {
      title: 'CMS ID',
      key: 'cms_id',
    },
    {
      title: 'Site ID',
      key: 'site_id',
      align: 'center',
      width: 120,
      // eslint-disable-next-line react/display-name
      render: (text, col, copyCompany) => {
        const key = `site_id_${copyCompany.id}`
        return (
          <Input
            size="small"
            onChange={({ target: { value } }) => {
              setSiteIds(prev => ({ ...prev, [key]: value }))
            }}
            value={siteIds[key] !== undefined ? siteIds[key] : copyCompany.site_id || ''}
          />
        )
      },
    },
  ]

  const list = (setFieldValue, columns) => {
    const columnsPrepared =
      isUserActionAllowed('update_copy_company_facilityprofile') && columns
        ? columns
        : primaryCopyCompanyColumns

    return (
      <Table
        columns={columnsPrepared}
        totalData={copyCompaniesList}
        onLoadData={getCopyCompanies}
        onFormatData={formatData}
        rowKey="id"
        formData={searchParams}
        loadingStatus={loadingStatus}
        loading={loadingStatus === LOADING_STATUS}
        customRowSelection={{
          key: 'is_primary',
          selectedRowKeys,
          disabled:
            !isUserActionAllowed('update_copy_company_facilityprofile') ||
            !isAutoDetectionEnabled,
          onSelect: newSelectedRowKeys => {
            setSelectedRowKeys([newSelectedRowKeys])
            setFieldValue('selected_id', newSelectedRowKeys)
          },
          selectedId: selectedRowKeys[0],
        }}
        totalCount={count}
        onPageChange={setPagination}
        pageSize={pagination.pageSize}
        pageSizeOptions={DEFAULT_COPY_COMPANY_PAGE_SIZE_OPTIONS}
      />
    )
  }

  return (
    <Formik
      initialValues={{
        ...initValues,
        auto_detection: isAutoDetection,
      }}
      enableReinitialize
      onSubmit={async (_, { setSubmitting }) => {
        try {
          const selectedKeyString = String(selectedRowKeys[0] || '')

          const cleanId = selectedKeyString.split('_')[0]

          const rawSiteIdFromKey = selectedKeyString.split('_')[1]
          const originalSiteIdFromKey =
            rawSiteIdFromKey === 'null' ? null : rawSiteIdFromKey

          const uniqueInputKey = `site_id_${selectedRowKeys[0]}`

          const currentRow = copyCompaniesList.find(
            c => getRowKey(c) === selectedRowKeys[0]
          )

          const originalVal =
            currentRow?.site_id !== undefined ? currentRow.site_id : originalSiteIdFromKey

          const actualSiteIdValue =
            siteIds[uniqueInputKey] !== undefined ? siteIds[uniqueInputKey] : originalVal

          const payload = {
            ...siteIds,
            selected_id: cleanId,
            [`site_id_${cleanId}`]: actualSiteIdValue,
          }

          await onSubmit(entityId, payload, isAutoDetectionEnabled)
          dispatch(resetCopyCompaniesState())
          onClose()
        } catch (err) {
          setSubmitting(false)
        }
      }}
    >
      {({ values, handleSubmit, setFieldValue, isSubmitting }) => (
        <Form onFinish={handleSubmit} initialValues={values} onKeyDown={handleKeyPress}>
          <FormObserver
            onChange={data => {
              if (data.auto_detection !== isAutoDetectionEnabled) {
                setFieldValue('auto_detection', isAutoDetectionEnabled)
              }
            }}
          />
          <Tabs
            defaultActiveKey="past"
            fullWidth
            theme="dark"
            destroyInactiveTabPane
            tabBarExtraContent={
              <Form layout="inline" className="sort-form" component="div">
                <Row>
                  <Col>
                    <Form.Item style={{ width: '300px', marginBottom: 0 }}>
                      <Input
                        prefix={
                          <Button type="link" size="small" icon={<SearchOutlined />} />
                        }
                        placeholder="Name, CMS ID, Site ID"
                        onPressEnter={handleSearch}
                      />
                    </Form.Item>
                  </Col>
                  {isUserActionAllowed('update_copy_company_auto_detection') && (
                    <Col>
                      <Form.Item
                        label="Auto Detection"
                        className="sort-form__select-field"
                        style={{ marginBottom: 0 }}
                      >
                        <Switch
                          defaultChecked={isAutoDetectionEnabled}
                          checked={isAutoDetectionEnabled}
                          name="auto_detection"
                          key="switch"
                          onChange={checked => {
                            setIsAutoDetectionEnabled(checked)
                            setFieldValue('auto_detection', checked)
                          }}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </Form>
            }
            tabsProps={{
              size: 'small',
              tabBarGutter: 24,
              onChange: handleChangeTab,
            }}
          >
            <Tab.TabPane tab="Past Copy Companies" key={TABS.PAST}>
              {list(setFieldValue)}
            </Tab.TabPane>
            <Tab.TabPane tab="All" key={TABS.ALL}>
              {list(setFieldValue, primaryCopyCompanyColumnsEditable)}
            </Tab.TabPane>
          </Tabs>
          <Row justify="space-between">
            <Col>
              <Button onClick={onClose}>Close window</Button>
            </Col>
            <Col>
              {(isUserActionAllowed('update_copy_company_facilityprofile') ||
                isUserActionAllowed('update_copy_company_auto_detection')) && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  disabled={isSaveDisabled || isSubmitting}
                  loading={isSubmitting}
                >
                  Save Changes
                </Button>
              )}
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  )
}

export default SetCopyCompanyForm
